import { apiClient } from "./apiClient";

export interface RelatorioPromocoesPizzasResumoTamanho {
  tamanhoNome: string;
  quantidade: number;
}

export interface RelatorioPromocoesPizzasResumo {
  promocaoKey: string;
  promocaoLabel: string;
  /** Itens/combos vendidos (SKU). */
  quantidadeItens?: number;
  /** Total de pizzas (combo 08+broto = 2 por item). */
  quantidadePizzas: number;
  porTamanho?: RelatorioPromocoesPizzasResumoTamanho[];
  quantidadeFracaoSabores: number;
  quantidadeSaboresDistintos: number;
}

export interface RelatorioPromocoesPizzasSabor {
  promocaoKey: string;
  promocaoLabel: string;
  saborNome: string;
  quantidadeFracoes: number;
}

export interface RelatorioPromocoesPizzasLinhaDetalhe {
  promocaoKey: string;
  promocaoLabel: string;
  orderId: number;
  orderItemId: number | null;
  itemNome: string;
  tamanhoExibicao: string;
  saborProporcaoTexto: string;
  lojaNome: string;
  quantidadeItem: number;
  referenciaEmISO: string | null;
}

/** Mesma ideia do relatório “Pizzas por sabor” — loja × tamanho × sabor × Q fracionada. */
export interface RelatorioPromocoesPizzaAgregadoLojaTamanhoSabor {
  promocaoKey: string;
  promocaoLabel: string;
  lojaNome: string;
  tamanhoNome: string;
  saborNome: string;
  quantidade: number;
}

export interface RelatorioPromocoesPizzasResponse {
  resumoPorPromocao: RelatorioPromocoesPizzasResumo[];
  saboresPorPromocao: RelatorioPromocoesPizzasSabor[];
  linhasDetalhe: RelatorioPromocoesPizzasLinhaDetalhe[];
  /** Novo formato tabela tipo «Pizzas por sabor». */
  agregadoLojaTamanhoSabor?: RelatorioPromocoesPizzaAgregadoLojaTamanhoSabor[];
}

export async function fetchRelatorioPromocoesPizzas(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}): Promise<RelatorioPromocoesPizzasResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  return apiClient.get(`/pedidos-integracao/relatorio-promocoes-pizzas?${qs}`);
}

export type RelatorioPedidosAgruparPor = "bairro" | "cep";

export interface RelatorioPedidosPorBairroLinha {
  lojaNome: string;
  cidade: string | null;
  bairroNome: string | null;
  quantidadeCeps: number | null;
  quantidadePedidos: number;
}

export const RELATORIO_PEDIDOS_BAIRRO_PAGE_SIZE = 50;

export interface RelatorioPedidosPorBairroResponse {
  agruparPor: RelatorioPedidosAgruparPor;
  linhas: RelatorioPedidosPorBairroLinha[];
  totalPedidos: number;
  totalLinhas: number;
  page: number;
  pageSize: number;
}

export async function fetchRelatorioPedidosPorBairro(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
  agruparPor?: RelatorioPedidosAgruparPor;
  page?: number;
  pageSize?: number;
  todasLinhas?: boolean;
}): Promise<RelatorioPedidosPorBairroResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
    agruparPor: params.agruparPor ?? "bairro",
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  if (params.todasLinhas === true) {
    qs.set("todasLinhas", "true");
  } else {
    qs.set("page", String(params.page ?? 1));
    qs.set(
      "pageSize",
      String(params.pageSize ?? RELATORIO_PEDIDOS_BAIRRO_PAGE_SIZE)
    );
  }
  return apiClient.get(
    `/pedidos-integracao/relatorio-pedidos-por-bairro?${qs}`
  );
}

export interface RelatorioPizzasPorTamanhoLinha {
  tamanhoNome: string;
  quantidadePizzas: number;
}

export interface RelatorioPizzasPorTamanhoResponse {
  linhas: RelatorioPizzasPorTamanhoLinha[];
  totalPizzas: number;
  totalPizzasExternas: number;
}

export async function fetchRelatorioPizzasPorTamanho(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}): Promise<RelatorioPizzasPorTamanhoResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  return apiClient.get(
    `/pedidos-integracao/relatorio-pizzas-por-tamanho?${qs}`
  );
}

export interface RelatorioRodizioPizzasPorTamanho {
  tamanhoNome: string;
  quantidadePizzas: number;
}

export interface RelatorioRodizioPizzasLinha {
  saborNome: string;
  tamanhoNome: string;
  tipo: string | null;
  quantidadePizzas: number;
}

export interface RelatorioRodizioPizzasResponse {
  totalPizzas: number;
  porTamanho: RelatorioRodizioPizzasPorTamanho[];
  linhas: RelatorioRodizioPizzasLinha[];
}

export async function fetchRelatorioRodizioPizzas(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}): Promise<RelatorioRodizioPizzasResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  return apiClient.get(`/pedidos-integracao/relatorio-rodizio-pizzas?${qs}`);
}

export interface RelatorioSalaoPizzasPorTamanho {
  tamanhoNome: string;
  quantidadePizzas: number;
}

export interface RelatorioSalaoPizzasLinha {
  saborNome: string;
  tamanhoNome: string;
  tipo: string | null;
  quantidadePizzas: number;
}

export interface RelatorioSalaoPizzasSecao {
  totalPizzas: number;
  porTamanho: RelatorioSalaoPizzasPorTamanho[];
  linhas: RelatorioSalaoPizzasLinha[];
}

export interface RelatorioSalaoPizzasResponse {
  totalPizzas: number;
  salao: RelatorioSalaoPizzasSecao;
  rodizio: RelatorioSalaoPizzasSecao;
}

export async function fetchRelatorioSalaoPizzas(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}): Promise<RelatorioSalaoPizzasResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  return apiClient.get(`/pedidos-integracao/relatorio-salao-pizzas?${qs}`);
}

export interface RelatorioSalaoBebidasLinha {
  lojaNome: string;
  bebidaNome: string;
  quantidade: number;
}

export interface RelatorioSalaoBebidasPorBebida {
  bebidaNome: string;
  quantidade: number;
}

export interface RelatorioSalaoBebidasResponse {
  totalBebidas: number;
  porBebida: RelatorioSalaoBebidasPorBebida[];
  linhas: RelatorioSalaoBebidasLinha[];
}

export async function fetchRelatorioSalaoBebidas(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}): Promise<RelatorioSalaoBebidasResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  return apiClient.get(`/pedidos-integracao/relatorio-salao-bebidas?${qs}`);
}

export interface RelatorioBebidasLinha {
  lojaNome: string;
  bebidaNome: string;
  quantidade: number;
}

export const RELATORIO_BEBIDAS_PAGE_SIZE = 50;

export interface RelatorioBebidasResponse {
  linhas: RelatorioBebidasLinha[];
  totalBebidas: number;
  totalLinhas: number;
  page: number;
  pageSize: number;
}

export async function fetchRelatorioBebidas(params: {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
  page?: number;
  pageSize?: number;
  todasLinhas?: boolean;
}): Promise<RelatorioBebidasResponse> {
  const qs = new URLSearchParams({
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
  });
  if (params.lojaId != null && params.lojaId > 0) {
    qs.set("lojaId", String(params.lojaId));
  }
  if (params.todasLinhas === true) {
    qs.set("todasLinhas", "true");
  } else {
    qs.set("page", String(params.page ?? 1));
    qs.set("pageSize", String(params.pageSize ?? RELATORIO_BEBIDAS_PAGE_SIZE));
  }
  return apiClient.get(`/pedidos-integracao/relatorio-bebidas?${qs}`);
}
